import { useEffect, useMemo, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { Minus, Plus } from "lucide-react";
import { FALLBACK_ITEM_IMG, MENU, MENU_TABS, QR_MENU_IMAGE, QR_MENU_LINK, slugify } from "@/data";
import { Reveal, MaskLine } from "@/components/Reveal";
import { MAX_QTY, useBasket } from "@/context/BasketContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MenuSection = () => {
  const [active, setActive] = useState(MENU_TABS[0].id);
  const { basket, bump, products } = useBasket();
  const [leftMap, setLeftMap] = useState({});

  // Live menu: owner-set prices win, hidden items drop out, custom items join their tab.
  const liveMenu = useMemo(() => {
    if (!products || !products.length) return MENU;
    const byName = Object.fromEntries(products.map((p) => [p.name, p]));
    const m = {};
    MENU_TABS.forEach((t) => {
      m[t.id] = (MENU[t.id] || [])
        .filter((i) => byName[i.name] === undefined || byName[i.name].available !== false)
        .map((i) => (byName[i.name] ? { ...i, price: byName[i.name].price } : i));
    });
    products.forEach((p) => {
      const isDefault = MENU_TABS.some((t) => (MENU[t.id] || []).some((i) => i.name === p.name));
      if (!isDefault && p.available !== false) {
        const cat = MENU_TABS.some((t) => t.id === p.category) ? p.category : MENU_TABS[0].id;
        m[cat].push({ name: p.name, price: p.price, desc: "", img: FALLBACK_ITEM_IMG, tag: "New" });
      }
    });
    return m;
  }, [products]);

  useEffect(() => {
    fetch(`${API}/bake`)
      .then((r) => r.json())
      .then((b) => setLeftMap(b.left || {}))
      .catch(() => {});
  }, []);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 220, damping: 26 });
  const sy = useSpring(my, { stiffness: 220, damping: 26 });

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="py-24 lg:py-36"
      onMouseMove={(e) => {
        mx.set(e.clientX);
        my.set(e.clientY);
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12 lg:mb-16">
          <div className="max-w-2xl">
            <Reveal>
              <p className="eyebrow mb-5">La Carte</p>
            </Reveal>
            <h2 className="font-display font-medium leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
              <MaskLine>Made at dawn,</MaskLine>
              <MaskLine delay={0.12}>
                gone by <span className="italic text-[#C85A32]">dusk.</span>
              </MaskLine>
            </h2>
            <p className="mt-5 text-base text-[#1C130E]/65 leading-relaxed max-w-xl" data-testid="menu-intro">
              Fifteen bakes, laminated and proofed the slow way — counted fresh every morning,
              each described exactly as you'd taste it. When the daily batch runs low, we say so.
            </p>
            <div
              data-testid="bulk-order-strip"
              className="mt-6 flex items-center gap-3 flex-wrap rounded-2xl border border-[#D4A359]/40 bg-[#D4A359]/[0.08] px-5 py-4 max-w-xl"
            >
              <p className="text-sm text-[#1C130E]/75 leading-relaxed">
                <span className="font-semibold text-[#1C130E]">Need more than 10 of an item?</span>{" "}
                We take bulk orders for cafés, parties and offices.
              </p>
              <a
                href="https://wa.me/919596766336?text=Bonjour!%20I%27d%20like%20a%20bulk%20order%20quote%20(more%20than%2010%20of%20an%20item)."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bulk-order-link"
                className="ml-auto shrink-0 rounded-full bg-[#1C130E] text-[#FDFBF7] px-5 py-2.5 text-xs font-semibold hover:bg-[#C85A32] transition-colors"
              >
                Ask on WhatsApp
              </a>
            </div>
          </div>
          <Reveal>
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Menu categories">
              {MENU_TABS.map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active === t.id}
                  data-testid={`menu-tab-${t.id}`}
                  onClick={() => setActive(t.id)}
                  className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-all duration-300 ${
                    active === t.id
                      ? "bg-[#1C130E] text-[#FDFBF7] border-[#1C130E]"
                      : "border-[rgba(28,19,14,0.2)] text-[#1C130E]/70 hover:border-[#C85A32] hover:text-[#C85A32]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <motion.ul
          key={active}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="border-b border-[rgba(28,19,14,0.12)]"
        >
          {liveMenu[active].map((item) => {
            const slug = slugify(item.name);
            const q = basket[item.name] || 0;
            const left = leftMap[item.name];
            const cap = left === undefined ? MAX_QTY : Math.min(MAX_QTY, left);
            return (
              <li
                key={item.name}
                data-testid={`menu-item-${slug}`}
                className="group border-t border-[rgba(28,19,14,0.12)] py-6 lg:py-8 flex items-center gap-5 cursor-default"
              >
                <img
                  alt={item.name}
                  loading="lazy"
                  className="lg:hidden w-16 h-16 rounded-xl object-cover shrink-0"
                  src={item.img}
                />
                <div className="min-w-0">
                  <h3 className="font-display font-medium text-2xl sm:text-3xl tracking-tight transition-colors duration-300 group-hover:text-[#C85A32]">
                    {item.name}
                  </h3>
                  <p className="mt-1 text-sm text-[#1C130E]/65 max-w-md leading-relaxed">{item.desc}</p>
                  {left !== undefined && left <= 5 && (
                    <span
                      data-testid={`menu-lowstock-${slug}`}
                      className={`mt-2 inline-block rounded-full px-3 py-1 font-mono-accent text-[10px] uppercase tracking-[0.18em] border ${
                        left === 0
                          ? "text-[#1C130E]/55 border-[rgba(28,19,14,0.2)] bg-[#1C130E]/[0.05]"
                          : "text-[#C85A32] border-[#C85A32]/40 bg-[#C85A32]/[0.07]"
                      }`}
                    >
                      {left === 0 ? "Sold out" : `Only ${left} left`}
                    </span>
                  )}
                </div>
                {item.tag && (
                  <span className="hidden sm:inline-block mr-3 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#C85A32] border border-[#C85A32]/40 rounded-full px-3 py-1.5">
                    {item.tag}
                  </span>
                )}
                <span className="ml-auto font-mono-accent text-sm text-[#C85A32] shrink-0" data-testid={`menu-price-${slug}`}>
                  ₹{item.price}
                </span>
                {q === 0 ? (
                  <button
                    type="button"
                    data-testid={`menu-add-${slug}`}
                    aria-label={`Add ${item.name} to basket`}
                    onClick={() => bump(item.name, 1)}
                    disabled={left === 0}
                    className="shrink-0 rounded-full bg-[#1C130E] text-[#FDFBF7] px-5 py-2.5 text-xs font-semibold hover:bg-[#C85A32] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#1C130E] transition-colors"
                  >
                    Add
                  </button>
                ) : (
                  <span className="shrink-0 flex items-center gap-2.5">
                    <button
                      type="button"
                      data-testid={`menu-add-${slug}-minus`}
                      aria-label={`Remove one ${item.name}`}
                      onClick={() => bump(item.name, -1)}
                      className="grid place-items-center w-8 h-8 rounded-full border border-[rgba(28,19,14,0.25)] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors"
                    >
                      <Minus size={13} />
                    </button>
                    <span data-testid={`menu-qty-${slug}`} className="w-5 text-center text-sm font-semibold">
                      {q}
                    </span>
                    <button
                      type="button"
                      data-testid={`menu-add-${slug}-plus`}
                      aria-label={`Add one ${item.name}`}
                      onClick={() => bump(item.name, 1)}
                      disabled={q >= cap}
                      className="grid place-items-center w-8 h-8 rounded-full bg-[#1C130E] text-[#FDFBF7] hover:bg-[#C85A32] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#1C130E] transition-colors"
                    >
                      <Plus size={13} />
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </motion.ul>

        <Reveal>
          <div
            data-testid="qr-menu-card"
            className="mt-14 rounded-3xl border border-[rgba(28,19,14,0.12)] bg-[#F8F3EA] px-8 py-10 lg:px-12 flex flex-col sm:flex-row items-center gap-8 justify-between"
          >
            <div className="max-w-md text-center sm:text-left">
              <p className="eyebrow mb-3">Le Menu Complet</p>
              <h3 className="font-display font-medium text-3xl sm:text-4xl tracking-tight">
                The full carte, <span className="italic text-[#C85A32]">in your phone.</span>
              </h3>
              <p className="mt-3 text-sm text-[#1C130E]/65 leading-relaxed">
                Point your camera at the code — our live digital menu opens instantly, always up to date with today's
                bake.
              </p>
            </div>
            <a
              href={QR_MENU_LINK}
              target="_blank"
              rel="noreferrer"
              data-testid="qr-menu-link"
              className="group shrink-0 rounded-2xl bg-white p-4 shadow-[0_20px_40px_-20px_rgba(28,19,14,0.35)] transition-transform duration-500 hover:-rotate-2 hover:scale-[1.03]"
            >
              <img
                alt="QR code — French Pie digital menu"
                data-testid="qr-menu-image"
                className="w-36 h-36 object-contain"
                src={QR_MENU_IMAGE}
              />
            </a>
          </div>
        </Reveal>
      </div>

      <motion.div
        className="fixed z-40 pointer-events-none hidden lg:block"
        style={{ left: sx, top: sy }}
        aria-hidden="true"
      >
        <div className="-translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#C85A32]/80" />
      </motion.div>
    </section>
  );
};
